import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useStore } from '../state/store';

type Tab = 'password' | 'users';

/**
 * Your account, and -- for an admin -- everyone else's.
 *
 * These endpoints existed and were tested from the start with no way to reach
 * them from the interface, which meant creating an account or resetting a
 * password required shell access to the box.
 */
export default function Account({ onClose }: { onClose: () => void }) {
  const user = useStore(s => s.user)!;
  const signOut = useStore(s => s.signOut);
  const notify = useStore(s => s.notify);
  const [tab, setTab] = useState<Tab>('password');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Account">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>Account</h2>
          <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="sub">{user.email} · {user.role}</p>

        {user.role === 'admin' && (
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'password'}
                    onClick={() => setTab('password')}>Your password</button>
            <button role="tab" aria-selected={tab === 'users'}
                    onClick={() => setTab('users')}>People</button>
          </div>
        )}

        {tab === 'password'
          ? <ChangePassword notify={notify} signOut={signOut} />
          : <Users notify={notify} meId={user.id} />}
      </div>
    </div>
  );
}

type Notify = (t: string, k?: 'info' | 'error') => void;

function ChangePassword({ notify, signOut }: { notify: Notify; signOut: () => Promise<void> }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = confirm.length > 0 && next !== confirm;

  return (
    <form onSubmit={async e => {
      e.preventDefault();
      if (tooShort || mismatch || busy) return;
      setBusy(true);
      try {
        await api.changePassword(current, next);
        // Changing a password revokes every login, including this one.
        notify('Password changed. Please sign in again.');
        await signOut();
      } catch (err) {
        notify((err as Error).message, 'error');
      } finally {
        setBusy(false);
      }
    }}>
      <div className="field">
        <label htmlFor="cur">Current password</label>
        <input id="cur" type="password" autoComplete="current-password"
               value={current} onChange={e => setCurrent(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="new">New password</label>
        <input id="new" type="password" autoComplete="new-password"
               value={next} onChange={e => setNext(e.target.value)} />
        <div className="hint" style={tooShort ? { color: 'var(--danger)' } : undefined}>
          At least 12 characters. Length is what resists cracking, so a short
          phrase beats a short scramble.
        </div>
      </div>
      <div className="field">
        <label htmlFor="conf">Confirm new password</label>
        <input id="conf" type="password" autoComplete="new-password"
               value={confirm} onChange={e => setConfirm(e.target.value)} />
        {mismatch && <div className="hint" style={{ color: 'var(--danger)' }}>
          These do not match.
        </div>}
      </div>
      <div className="hint">
        Changing this signs you out everywhere, which is the point of doing it.
      </div>
      <div className="modal-actions">
        <button className="btn primary"
                disabled={busy || !current || next.length < 12 || next !== confirm}>
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}

function Users({ notify, meId }: { notify: Notify; meId: string }) {
  const [users, setUsers] = useState<Awaited<ReturnType<typeof api.listUsers>> | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('producer');
  const [resetting, setResetting] = useState<{ id: string; email: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const load = useCallback(() => {
    api.listUsers().then(setUsers).catch(e => {
      notify((e as Error).message, 'error');
      setUsers([]);
    });
  }, [notify]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    try { await fn(); load(); } catch (e) { notify((e as Error).message, 'error'); }
  };

  return (
    <>
      <div className="field">
        <label>People with an account</label>
        {users === null ? <div className="muted">Loading…</div> : (
          <div style={{ display: 'grid', gap: 6 }}>
            {users.map(u => (
              <div className="row" key={u.id}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <strong>{u.displayName}</strong>
                  <div className="stage-meta">{u.email}</div>
                </span>
                <span className="chip">{u.role}</span>
                {u.disabled && <span className="chip chip-danger">disabled</span>}
                <button className="btn sm"
                        onClick={() => setResetting({ id: u.id, email: u.email })}>
                  Reset
                </button>
                <button className="btn sm" disabled={u.id === meId}
                        title={u.id === meId ? 'You cannot disable your own account' : undefined}
                        onClick={() => run(() => api.setUserDisabled(u.id, !u.disabled))}>
                  {u.disabled ? 'Enable' : 'Disable'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <form className="field" onSubmit={e => {
        e.preventDefault();
        void run(async () => {
          await api.createUser(email.trim(), password, name.trim() || email.trim(), role);
          notify(`Created ${email.trim()}.`);
          setEmail(''); setName(''); setPassword('');
        });
      }}>
        <label>Add someone</label>
        <div style={{ display: 'grid', gap: 6 }}>
          <input type="email" value={email} placeholder="their@email.com"
                 aria-label="New account email" onChange={e => setEmail(e.target.value)} />
          <input value={name} placeholder="Their name"
                 aria-label="New account name" onChange={e => setName(e.target.value)} />
          <input type="password" value={password} placeholder="Temporary password (12+ characters)"
                 aria-label="New account password" onChange={e => setPassword(e.target.value)} />
          <div className="row">
            <select value={role} onChange={e => setRole(e.target.value)}
                    aria-label="Role" style={{ width: 140 }}>
              <option value="producer">Producer</option>
              <option value="admin">Admin</option>
            </select>
            <button className="btn primary"
                    disabled={!email.trim() || password.length < 12}>Create</button>
          </div>
        </div>
        <div className="hint">
          There is no public sign-up, so accounts are made here. Crew joining a
          show never need one.
        </div>
      </form>

      {resetting && (
        <div className="overlay" style={{ zIndex: 80 }}
             onClick={e => e.target === e.currentTarget && setResetting(null)}>
          <div className="modal" style={{ maxWidth: 400 }} role="dialog"
               aria-label="Reset password">
            <h2>Reset password</h2>
            <p className="sub">
              For {resetting.email}. They will be signed out everywhere, so tell
              them what you set.
            </p>
            <div className="field">
              <label htmlFor="reset-pw">New password</label>
              <input id="reset-pw" type="text" value={newPassword} autoFocus
                     placeholder="At least 12 characters"
                     onChange={e => setNewPassword(e.target.value)} />
              <div className="hint">
                Shown rather than hidden: you have to be able to read it back to them.
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => { setResetting(null); setNewPassword(''); }}>
                Cancel
              </button>
              <button className="btn danger" disabled={newPassword.length < 12}
                      onClick={() => {
                        const target = resetting;
                        setResetting(null);
                        void run(async () => {
                          await api.setUserPassword(target.id, newPassword);
                          notify(`Password reset for ${target.email}.`);
                          setNewPassword('');
                        });
                      }}>
                Reset it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
